import { useMemo, useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NonSvoltaDialog from "./NonSvoltaDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { ChevronLeft, ChevronRight, Pencil, AlertTriangle, Trash2, Route, MapPin, Info, Navigation, ExternalLink, Ban } from "lucide-react";
import { toast } from "sonner";
import AffiancamentoButton from "@/components/AffiancamentoButton";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const TYPE_LABEL = { affiancamento_agenzia: "Affiancamento agenzia", visita_partner: "Visita", telefonata: "Telefonata", videocall: "Video call", email_contatto: "Email / contatto", review_partner: "Review Partner" };

function EndpointSuggest({ q, picked, onPick }) {
  const [sugs, setSugs] = useState([]);
  useEffect(() => {
    if (picked || (q || "").trim().length < 4) { setSugs([]); return undefined; }
    const t = setTimeout(() => api.get("/geocode/suggest", { params: { q } }).then((r) => setSugs(r.data.suggestions || [])).catch(() => setSugs([])), 400);
    return () => clearTimeout(t);
  }, [q, picked]);
  if (!sugs.length) return null;
  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="cal-endpoint-suggestions">
      {sugs.map((s, i) => (
        <button key={i} type="button" onClick={() => { onPick(s); setSugs([]); }} className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-muted" data-testid={`cal-endpoint-sug-${i}`}>
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />{s.label}
        </button>
      ))}
    </div>
  );
}


const isAgenzia = (it) => it.actor === "agenzia";
const isResp = (it) => it.actor === "responsabile_commerciale" || it.type === "affiancamento_agenzia";
const agencyKey = (it) => it.agency_name || it.resolved_agency_name || null;
const chipHex = (it, ownerColors = {}) => isResp(it) ? "#F97316" : (ownerColors[agencyKey(it)]?.hex || (isAgenzia(it) ? "#3B82F6" : "#F97316"));
const chipText = (hex) => {
  const h = String(hex || "").replace("#", "");
  let lum = 0;
  if (h.length >= 6) {
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }
  return lum > 0.58 ? "#111827" : "#ffffff";
};
const targetName = (it) => (it.partner_name || it.prospect_name || it.agency_name) || "—";
const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfWeek = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
const sameDay = (a, b) => fmt(a) === fmt(b);

// Punto navigabile per Google Maps: coordinate se disponibili, altrimenti indirizzo testuale.
const gmapsPoint = (o) => {
  if (o && o.lat != null && o.lng != null) return `${o.lat},${o.lng}`;
  if (o && (o.address || o.city)) return [o.address, o.city].filter(Boolean).join(", ");
  return null;
};
// URL "direzioni" con l'intero giro della giornata: Sede → tappe → Sede.
const gmapsTourUrl = (ag) => {
  const base = (ag.base_lat != null && ag.base_lng != null) ? `${ag.base_lat},${ag.base_lng}` : null;
  const stopPts = (ag.stops || []).map(gmapsPoint).filter(Boolean);
  if (!stopPts.length) return null;
  const origin = base || stopPts[0];
  const destination = base || stopPts[stopPts.length - 1];
  const waypoints = base ? stopPts : stopPts.slice(1, stopPts.length - 1);
  const params = new URLSearchParams({ api: "1", origin, destination, travelmode: "driving" });
  if (waypoints.length) params.set("waypoints", waypoints.join("|"));
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};
const gmapsSearchUrl = (s) => {
  const p = gmapsPoint(s);
  return p ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p)}` : null;
};

export default function AgendaCalendar({ items, mode, ownerColors = {}, onChanged, readOnly = false, kmFilters = null }) {
  const { user } = useAuth();
  const canEdit = !readOnly && ["admin", "commercial_manager", "agency", "head_of_partner", "back_office", "technical"].includes(user?.role);
  const canDelete = ["admin", "back_office"].includes(user?.role) || user?.role_actual === "back_office";
  const [cursor, setCursor] = useState(new Date());
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [ef, setEf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [kmDays, setKmDays] = useState({});
  const [kmDialog, setKmDialog] = useState(null);
  const [kmRange, setKmRange] = useState(null);
  const [editStop, setEditStop] = useState(null);
  const [stopSaving, setStopSaving] = useState(false);
  const today = new Date();

  useEffect(() => {
    if (mode !== "2weeks") { setKmDays({}); setKmRange(null); return; }
    const ws2 = startOfWeek(cursor);
    const e2 = new Date(ws2); e2.setDate(ws2.getDate() + 13);
    const start = fmt(ws2), end = fmt(e2);
    setKmRange({ start, end });
    let cancel = false;
    api.get("/agenda/km-stima", { params: { start, end, ...(kmFilters || {}) } })
      .then((r) => { if (!cancel) setKmDays(r.data.days || {}); })
      .catch(() => { if (!cancel) setKmDays({}); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, cursor, kmFilters?.actor, kmFilters?.agency, kmFilters?.agent, kmFilters?.status, kmFilters?.agency_id]);

  const reloadKm = async () => {
    if (!kmRange) return;
    try {
      const r = await api.get("/agenda/km-stima", { params: { ...kmRange, ...(kmFilters || {}) } });
      const days = r.data.days || {};
      setKmDays(days);
      setKmDialog((cur) => (cur ? { date: cur.date, ...(days[cur.date] || { km: 0, agents: [] }) } : cur));
    } catch { /* noop */ }
  };

  const saveStopGeo = async () => {
    if (!editStop) return;
    setStopSaving(true);
    try {
      const r = await api.post("/agenda/stop-geo", {
        kind: editStop.kind, id: editStop.id,
        address: editStop.address, city: editStop.city, province: editStop.province,
        as_sede: !!editStop.as_sede,
      });
      if (r.data.geocoded) toast.success(`Geolocalizzato: ${editStop.name}`);
      else toast.warning("Indirizzo salvato, ma non è stato possibile geolocalizzarlo con precisione.");
      setEditStop(null);
      await reloadKm();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella geolocalizzazione"); }
    finally { setStopSaving(false); }
  };

  const openDetail = (it) => { setDetail(it); setEditMode(false); };
  const [endEdit, setEndEdit] = useState(null);
  const [endSaving, setEndSaving] = useState(false);
  const saveEndEdit = async () => {
    if (!endEdit) return;
    setEndSaving(true);
    try {
      await api.post("/agenda/trip-endpoint", {
        scope: endEdit.scope, which: endEdit.which, date: endEdit.date, group: endEdit.agent,
        agency_id: endEdit.agency_id, address: endEdit.address, city: endEdit.city, province: endEdit.province,
        ...(endEdit.lat != null ? { lat: endEdit.lat, lng: endEdit.lng } : {}),
      });
      toast.success(endEdit.scope === "sede" ? "Sede aggiornata" : "Punto aggiornato per questo tragitto");
      setEndEdit(null);
      await reloadKm();
    } catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
    finally { setEndSaving(false); }
  };
  const startEdit = () => {
    setEf({
      date: detail.date || "", time: detail.time || "", status: detail.status || "pianificato",
      outcome: detail.outcome || "", notes: detail.notes || "",
      prossima_azione: detail.prossima_azione || "", data_prossima_azione: detail.data_prossima_azione || "",
      affiancamento_richiesto: !!(detail.affiancamento && detail.affiancamento.attivo),
    });
    setEditMode(true);
  };
  const saveEdit = async () => {
    if (!ef.date) { toast.error("La data è obbligatoria"); return; }
    setSaving(true);
    try {
      await api.put(`/activities/${detail.id}`, {
        type: detail.type, date: ef.date, time: ef.time, status: ef.status,
        agency_id: detail.agency_id || null, partner_id: detail.partner_id || null, prospect_id: detail.prospect_id || null,
        owner_id: detail.owner_id || null, actor: detail.actor || "responsabile_commerciale",
        outcome: ef.outcome, notes: ef.notes,
        prossima_azione: ef.prossima_azione, data_prossima_azione: ef.data_prossima_azione || null,
        affiancamento_richiesto: !!ef.affiancamento_richiesto,
      });
      toast.success("Appuntamento aggiornato");
      setDetail(null); setEditMode(false); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  const [nsAct, setNsAct] = useState(null);
  const nonSvoltaDetail = () => setNsAct(detail);
  const removeDetail = async () => {
    if (!detail?.id) return;
    if (!window.confirm("Eliminare questo appuntamento? L'attività verrà rimossa dall'agenda.")) return;
    setSaving(true);
    try {
      await api.delete(`/activities/${detail.id}`);
      toast.success("Appuntamento eliminato");
      setDetail(null); setEditMode(false); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'eliminazione"); }
    finally { setSaving(false); }
  };

  const byDate = useMemo(() => {
    const m = {};
    const seen = new Set();
    items.forEach((it) => {
      if (!it.date || (it.id && seen.has(it.id))) return;
      if (it.id) seen.add(it.id);
      (m[it.date] = m[it.date] || []).push(it);
    });
    Object.values(m).forEach((arr) => arr.sort((x, y) => (x.time || "").localeCompare(y.time || "")));
    return m;
  }, [items]);

  const [openDays, setOpenDays] = useState({});
  const toggleDay = (k) => setOpenDays((s) => ({ ...s, [k]: !s[k] }));
  const isVisita = (it) => it.type === "affiancamento_agenzia" || ((it.type === "visita" || it.type === "visita_partner") && !!it.time);
  const affBadge = (it) => it.type === "affiancamento_agenzia" || !!(it.affiancamento && it.affiancamento.attivo);

  const chipCompact = (it) => (
    <button key={it.id} onClick={() => openDetail(it)} data-testid={`cal-chip-${it.id}`} style={{ backgroundColor: chipHex(it, ownerColors), color: chipText(chipHex(it, ownerColors)) }} className="relative mb-0.5 block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium transition-opacity hover:opacity-80">
      {it.affiancamento_senza_nota && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-400 ring-1 ring-white" title="Affiancamento senza nota" />}
      {it.time ? `${it.time} ` : ""}{affBadge(it) && <span className="mr-1 rounded bg-black/25 px-1 text-[8px] font-bold uppercase">Aff</span>}{targetName(it)}
    </button>
  );
  const chipTall = (it) => (
    <button key={it.id} onClick={() => openDetail(it)} data-testid={`cal-chip-${it.id}`} style={{ backgroundColor: chipHex(it, ownerColors), color: chipText(chipHex(it, ownerColors)) }} className="relative block w-full rounded px-1.5 py-1 text-left text-[10px] font-medium transition-opacity hover:opacity-80">
      {it.affiancamento_senza_nota && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-400 ring-1 ring-white" title="Affiancamento senza nota" />}
      <div className="font-semibold">{it.time || "—"}</div>
      <div className="truncate">{affBadge(it) && <span className="mr-1 rounded bg-black/25 px-1 text-[8px] font-bold uppercase align-middle">Aff</span>}{targetName(it)}</div>
      {it.type === "affiancamento_agenzia" && (it.partner_name || it.prospect_name) && it.agency_name && <div className="truncate text-[9px] font-medium opacity-80">Affiancamento: {it.agency_name}</div>}
    </button>
  );
  const renderCell = (list, dayKey, tall) => {
    const others = list.filter((it) => !isVisita(it));
    const visite = list.filter(isVisita);
    const chip = tall ? chipTall : chipCompact;
    const open = !!openDays[dayKey];
    return (
      <>
        {others.length > 0 && (
          <div className="mb-1">
            <button onClick={(e) => { e.stopPropagation(); toggleDay(dayKey); }} className="flex w-full items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted/70" data-testid={`cal-others-toggle-${dayKey}`}>
              <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} /> {others.length} attività
            </button>
            {open && <div className="mt-1 space-y-0.5" data-testid={`cal-others-list-${dayKey}`}>{others.map(chip)}</div>}
          </div>
        )}
        {others.length > 0 && visite.length > 0 && <div className="my-1 border-t border-border" />}
        <div className={tall ? "space-y-1" : ""}>
          {visite.map(chip)}
          {list.length === 0 && <div className="pt-2 text-center text-[10px] text-muted-foreground">—</div>}
        </div>
      </>
    );
  };

  const move = (dir) => {
    const c = new Date(cursor);
    if (mode === "week") c.setDate(c.getDate() + dir * 7);
    else if (mode === "2weeks") c.setDate(c.getDate() + dir * 14);
    else c.setMonth(c.getMonth() + dir);
    setCursor(c);
  };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const ws = startOfWeek(cursor);
  const rangeTitle = (days) => { const e = new Date(ws); e.setDate(ws.getDate() + days); return `${pad(ws.getDate())} ${MONTHS[ws.getMonth()].slice(0, 3)} – ${pad(e.getDate())} ${MONTHS[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`; };
  const title = mode === "week" ? rangeTitle(6) : mode === "2weeks" ? rangeTitle(13) : `${MONTHS[month]} ${year}`;

  return (
    <div data-testid="agenda-calendar">
      <NonSvoltaDialog activity={nsAct} onClose={() => setNsAct(null)} onDone={() => { setDetail(null); onChanged?.(); }} />
      <div className="mb-3 flex items-center justify-between">
        <div className="font-heading text-lg font-bold tracking-tight" data-testid="calendar-title">{title}</div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today">Oggi</Button>
          <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => <div key={w} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{w}</div>)}

        {mode === "month" && Array.from({ length: 42 }, (_, i) => {
          const d = new Date(startOfWeek(new Date(year, month, 1)));
          d.setDate(d.getDate() + i);
          const inMonth = d.getMonth() === month;
          const list = byDate[fmt(d)] || [];
          return (
            <div key={i} className={`min-h-[96px] rounded-md border border-border p-1 ${inMonth ? "" : "bg-muted/30 opacity-60"} ${sameDay(d, today) ? "ring-1 ring-primary" : ""}`} data-testid={`cal-day-${fmt(d)}`}>
              <div className="mb-0.5 text-[11px] font-mono text-muted-foreground">{d.getDate()}</div>
              {renderCell(list, fmt(d), false)}
            </div>
          );
        })}

        {mode === "week" && Array.from({ length: 7 }, (_, i) => {
          const d = new Date(ws);
          d.setDate(ws.getDate() + i);
          const list = byDate[fmt(d)] || [];
          return (
            <div key={i} className={`min-h-[280px] rounded-md border border-border p-1.5 ${sameDay(d, today) ? "ring-1 ring-primary" : ""}`} data-testid={`cal-day-${fmt(d)}`}>
              <div className="mb-1 text-center text-[11px] font-mono text-muted-foreground">{d.getDate()}/{d.getMonth() + 1}</div>
              {renderCell(list, fmt(d), true)}
            </div>
          );
        })}
        {mode === "2weeks" && Array.from({ length: 14 }, (_, i) => {
          const d = new Date(ws);
          d.setDate(ws.getDate() + i);
          const list = byDate[fmt(d)] || [];
          const kd = kmDays[fmt(d)];
          return (
            <div key={i} className={`min-h-[180px] rounded-md border border-border p-1.5 ${sameDay(d, today) ? "ring-1 ring-primary" : ""}`} data-testid={`cal-day-${fmt(d)}`}>
              <div className="mb-1 text-center text-[11px] font-mono text-muted-foreground">{d.getDate()}/{d.getMonth() + 1}</div>
              {renderCell(list, fmt(d), true)}
              {kd && (kd.km > 0 || (kd.agents || []).length > 0) && (
                <button onClick={(e) => { e.stopPropagation(); setKmDialog({ date: fmt(d), ...kd }); }}
                  className="mt-1.5 flex w-full items-center justify-center gap-1 rounded bg-primary/10 px-1 py-0.5 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/20"
                  data-testid={`cal-km-${fmt(d)}`} title="Stima km di viaggio — clicca per il dettaglio">
                  <Route className="h-3 w-3" /> ~{kd.km} km
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-orange-500" /> Resp. Commerciale</span>
        {Object.values(ownerColors).map((oc, i) => (
          <span key={i} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ backgroundColor: oc.hex }} /> {oc.name}</span>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setEditMode(false); } }}>
        <DialogContent data-testid="calendar-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">{targetName(detail)}<Badge className={`border text-[10px] ${isAgenzia(detail) ? "border-blue-300 bg-blue-100 text-blue-800" : "border-orange-300 bg-orange-100 text-orange-800"}`}>{isAgenzia(detail) ? "Agenzia" : "Resp. Commerciale"}</Badge></DialogTitle><DialogDescription>{editMode ? "Modifica appuntamento" : "Dettaglio attività"}</DialogDescription></DialogHeader>
              {!editMode ? (
                <div className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Tipo:</span> {TYPE_LABEL[detail.type]}</div>
                  <div><span className="text-muted-foreground">Data / ora:</span> {detail.date}{detail.time ? ` · ${detail.time}` : ""}</div>
                  {detail.resolved_agency_name && <div><span className="text-muted-foreground">Agenzia:</span> {detail.resolved_agency_name}</div>}
                  {detail.type === "affiancamento_agenzia" && detail.agency_name && <div><span className="text-muted-foreground">In affiancamento con:</span> {detail.agency_name}</div>}
                  <div><span className="text-muted-foreground">Stato:</span> {detail.status === "svolto" ? "Svolto" : detail.status === "non_svolta" ? "Non svolta" : "Pianificato"}{detail.motivo_non_svolta ? ` · ${detail.motivo_non_svolta}` : ""}</div>
                  {detail.modified_by && <div className="text-xs text-muted-foreground" data-testid="cal-modified">Modificata da {detail.modified_by}{detail.modified_at ? ` il ${new Date(detail.modified_at).toLocaleString("it-IT")}` : ""}</div>}
                  {detail.owner_name && <div><span className="text-muted-foreground">Responsabile:</span> {detail.owner_name}</div>}
                  {detail.outcome && <div><span className="text-muted-foreground">Esito:</span> {detail.outcome}</div>}
                  {detail.prossima_azione && <div><span className="text-muted-foreground">Prossima azione:</span> {detail.prossima_azione}{detail.data_prossima_azione ? ` (${detail.data_prossima_azione})` : ""}</div>}
                  {detail.notes && <div><span className="text-muted-foreground">Note:</span> {detail.notes}</div>}
                  {!readOnly && (detail.type === "visita_partner" || detail.type === "visita" || detail.type === "affiancamento_agenzia") && (
                    <div className="pt-1"><AffiancamentoButton visit={detail} onDone={() => { setDetail(null); onChanged?.(); }} /></div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label className="text-xs">Data</Label><DatePicker value={ef.date} onChange={(v) => setEf((s) => ({ ...s, date: v }))} data-testid="cal-edit-date" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Orario</Label><Input type="time" value={ef.time} onChange={(e) => setEf((s) => ({ ...s, time: e.target.value }))} data-testid="cal-edit-time" /></div>
                  <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Stato</Label>
                    <Select value={ef.status} onValueChange={(v) => setEf((s) => ({ ...s, status: v }))}>
                      <SelectTrigger data-testid="cal-edit-status"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pianificato">Pianificato</SelectItem><SelectItem value="svolto">Svolto</SelectItem><SelectItem value="non_svolta">Non svolta</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {ef.status === "svolto" && <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Esito</Label><Input value={ef.outcome} onChange={(e) => setEf((s) => ({ ...s, outcome: e.target.value }))} data-testid="cal-edit-outcome" /></div>}
                  <div className="space-y-1.5"><Label className="text-xs">Prossima azione</Label><Input value={ef.prossima_azione} onChange={(e) => setEf((s) => ({ ...s, prossima_azione: e.target.value }))} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Data prossima azione</Label><DatePicker value={ef.data_prossima_azione} onChange={(v) => setEf((s) => ({ ...s, data_prossima_azione: v }))} placeholder="—" /></div>
                  <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Note</Label><Textarea rows={3} value={ef.notes} onChange={(e) => setEf((s) => ({ ...s, notes: e.target.value }))} data-testid="cal-edit-notes" /></div>
                  {(detail.type === "visita_partner" || detail.type === "visita") && (
                    detail.affiancamento?.attivo ? (
                      <div className="sm:col-span-2 rounded-md border border-orange-400/40 bg-orange-500/10 p-2.5 text-xs text-orange-600 dark:text-orange-400" data-testid="cal-edit-aff-attivo">
                        Affiancamento già richiesto{detail.affiancamento.responsabile_name ? ` · Responsabile: ${detail.affiancamento.responsabile_name}` : ""}
                      </div>
                    ) : (
                      <label className="sm:col-span-2 flex cursor-pointer items-center gap-2 rounded-md border border-orange-400/40 bg-orange-500/5 p-2.5" data-testid="cal-edit-aff-row">
                        <Checkbox checked={ef.affiancamento_richiesto} onCheckedChange={(v) => setEf((s) => ({ ...s, affiancamento_richiesto: !!v }))} className="border-orange-400 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600" data-testid="cal-edit-aff-checkbox" />
                        <span className="text-xs">Richiedi <strong>affiancamento</strong> del Responsabile Commerciale su questa visita</span>
                      </label>
                    )
                  )}
                </div>
              )}
              {canEdit && (
                <DialogFooter>
                  {!editMode ? (
                    <>
                      {!detail.virtual && detail.status === "pianificato" && <Button variant="outline" onClick={nonSvoltaDetail} disabled={saving} className="border-amber-500/40 text-amber-500 hover:bg-amber-500/10" data-testid="cal-nonsvolta-btn"><Ban className="mr-2 h-4 w-4" /> Non svolta</Button>}
                      {!detail.virtual && canDelete && <Button variant="outline" onClick={removeDetail} disabled={saving} className="mr-auto border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="cal-delete-btn"><Trash2 className="mr-2 h-4 w-4" /> Elimina</Button>}
                      <Button onClick={startEdit} data-testid="cal-edit-btn"><Pencil className="mr-2 h-4 w-4" /> Modifica</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setEditMode(false)}>Annulla</Button>
                      <Button onClick={saveEdit} disabled={saving} data-testid="cal-edit-save">{saving ? "Salvataggio…" : "Salva"}</Button>
                    </>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!kmDialog} onOpenChange={(o) => { if (!o) setKmDialog(null); }}>
        <DialogContent data-testid="cal-km-dialog" className="max-h-[85vh] overflow-y-auto">
          {kmDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Route className="h-5 w-5 text-primary" /> Stima km di viaggio · {kmDialog.date.split("-").reverse().join("/")}</DialogTitle>
                <DialogDescription>Totale giornata: <b className="text-foreground">~{kmDialog.km} km</b> (stima indicativa). Partenza e rientro dalla sede.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {(kmDialog.agents || []).map((ag, i) => (
                  <div key={i} className="rounded-md border border-border p-2.5" data-testid={`cal-km-agent-${i}`}>
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="font-semibold">{ag.agent}</span>
                      <Badge variant="outline" className="text-[11px]">~{ag.km} km</Badge>
                    </div>
                    {gmapsTourUrl(ag) && (
                      <a href={gmapsTourUrl(ag)} target="_blank" rel="noopener noreferrer"
                        className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-2 py-1.5 text-[11px] font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                        data-testid={`cal-km-gmaps-tour-${i}`}>
                        <Navigation className="h-3.5 w-3.5" /> Apri il giro in Google Maps
                      </a>
                    )}
                    {ag.no_base && (
                      <div className="mb-1.5 flex items-start gap-1.5 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-600 dark:text-amber-400">
                        <MapPin className="mt-0.5 h-3 w-3 shrink-0" /> Sede di partenza non impostata{canEdit ? ": tocca «Imposta partenza» qui sotto per inserirla." : "."}
                      </div>
                    )}
                    <div className="divide-y divide-border/50">
                      {(
                        <div className="flex items-center gap-1.5 py-1 text-xs">
                          <button type="button" disabled={!canEdit}
                            onClick={() => canEdit && setEndEdit({ date: kmDialog.date, which: "partenza", agent: ag.agent, is_agency: ag.is_agency, agency_id: ag.agency_id, scope: null, address: "", city: "", province: "" })}
                            className={`flex items-center gap-1.5 text-left text-muted-foreground ${canEdit ? "hover:text-primary" : ""}`} data-testid={`cal-km-partenza-${i}`}>
                            <MapPin className="h-3 w-3 shrink-0 text-primary" /> Partenza · <span className={ag.no_base ? "font-semibold text-amber-600 dark:text-amber-400" : "text-foreground"}>{ag.part_custom ? ag.part_label : ag.no_base ? "Imposta partenza" : `Sede (${ag.base_label})`}</span>
                            {ag.part_custom && <span className="rounded bg-primary/15 px-1 text-[9px] font-semibold text-primary">personalizzato</span>}
                            {canEdit && <Pencil className="h-2.5 w-2.5 shrink-0" />}
                          </button>
                        </div>
                      )}
                      {(ag.stops || []).map((s, j) => (
                        <div key={j} className="flex items-start justify-between gap-2 py-1.5">
                          <div className="min-w-0">
                            <button type="button" disabled={!s.id}
                              onClick={() => s.id && setEditStop({ kind: s.kind, id: s.id, name: s.name, address: s.address || "", city: s.city || "", province: s.province || "", as_sede: false })}
                              className={`block max-w-full truncate text-left text-xs font-medium ${s.id ? "text-foreground hover:text-primary hover:underline" : "text-foreground"}`}
                              data-testid={`cal-km-stop-${j}`}>
                              {s.name}
                            </button>
                            <button type="button" disabled={!s.id}
                              onClick={() => s.id && setEditStop({ kind: s.kind, id: s.id, name: s.name, address: s.address || "", city: s.city || "", province: s.province || "", as_sede: false })}
                              className={`flex items-center gap-1 text-left text-[11px] ${s.id ? "hover:text-primary" : ""} ${s.geocoded ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}`}>
                              {s.id && <Pencil className="h-2.5 w-2.5 shrink-0" />}
                              <span className="truncate">{s.address ? `${s.address}${s.city ? ", " + s.city : ""}` : (s.city || "indirizzo mancante")}{!s.geocoded ? " · da geolocalizzare" : ""}</span>
                            </button>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {gmapsSearchUrl(s) && (
                              <a href={gmapsSearchUrl(s)} target="_blank" rel="noopener noreferrer" title="Apri in Google Maps"
                                onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-primary" data-testid={`cal-km-stop-gmaps-${j}`}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            <span className="font-mono text-xs text-primary">{s.km_from_prev != null ? `+${s.km_from_prev} km` : "—"}</span>
                          </div>
                        </div>
                      ))}
                      {(ag.return_km != null || canEdit) && (
                        <div className="flex items-center justify-between gap-2 py-1 text-xs text-muted-foreground">
                          <button type="button" disabled={!canEdit}
                            onClick={() => canEdit && setEndEdit({ date: kmDialog.date, which: "rientro", agent: ag.agent, is_agency: ag.is_agency, agency_id: ag.agency_id, scope: null, address: "", city: "", province: "" })}
                            className={`flex items-center gap-1.5 text-left ${canEdit ? "hover:text-primary" : ""}`} data-testid={`cal-km-rientro-${i}`}>
                            <MapPin className="h-3 w-3 shrink-0 text-primary" /> Rientro · {ag.ret_custom ? ag.ret_label : ag.no_base ? <span className="font-semibold text-amber-600 dark:text-amber-400">Imposta rientro</span> : ag.ret_as_part ? `Come partenza (${ag.ret_label})` : `Sede (${ag.base_label})`}
                            {ag.ret_custom && <span className="rounded bg-primary/15 px-1 text-[9px] font-semibold text-primary">personalizzato</span>}
                            {canEdit && <Pencil className="h-2.5 w-2.5 shrink-0" />}
                          </button>
                          {ag.return_km != null && <span className="shrink-0 font-mono text-primary">+{ag.return_km} km</span>}
                        </div>
                      )}
                    </div>
                    {(ag.stops || []).length === 0 && <div className="text-xs text-muted-foreground">Nessuna tratta calcolabile.</div>}
                  </div>
                ))}
                <div className="space-y-1.5 rounded-md border border-primary/30 bg-primary/5 p-2.5 text-[11px] text-muted-foreground" data-testid="cal-km-notes">
                  <div className="flex items-start gap-1.5"><Info className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> La stima verrà inviata all'amministrazione per la verifica dei rimborsi.</div>
                  <div className="flex items-start gap-1.5"><MapPin className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> Per una stima corretta, inserisci l'indirizzo della tua sede/abitazione nel tuo profilo agenzia.</div>
                  <div className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> Se l'indirizzo di un cliente/prospect non è corretto, cliccalo qui sopra per modificarlo e rigeolocalizzarlo.</div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editStop} onOpenChange={(o) => { if (!o) setEditStop(null); }}>
        <DialogContent data-testid="cal-stop-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Indirizzo · {editStop?.name}</DialogTitle>
            <DialogDescription>Modifica l'indirizzo dell'anagrafica e rigeolocalizza, oppure indica una sede operativa diversa da usare per il calcolo dei km.</DialogDescription>
          </DialogHeader>
          {editStop && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">Indirizzo</Label><Input value={editStop.address} onChange={(e) => setEditStop((s) => ({ ...s, address: e.target.value }))} placeholder="Via e numero civico" data-testid="cal-stop-address" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-1.5"><Label className="text-xs">Città</Label><Input value={editStop.city} onChange={(e) => setEditStop((s) => ({ ...s, city: e.target.value }))} data-testid="cal-stop-city" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Provincia</Label><Input value={editStop.province} onChange={(e) => setEditStop((s) => ({ ...s, province: e.target.value }))} placeholder="BS" data-testid="cal-stop-prov" /></div>
              </div>
              {editStop.kind === "partner" && (
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-input p-2.5 text-xs" data-testid="cal-stop-as-sede-row">
                  <Checkbox checked={!!editStop.as_sede} onCheckedChange={(v) => setEditStop((s) => ({ ...s, as_sede: !!v }))} data-testid="cal-stop-as-sede" />
                  <span>Salva come <strong>sede operativa</strong> (diversa dalla sede in anagrafica) e usala per il calcolo dei km</span>
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditStop(null)}>Annulla</Button>
            <Button onClick={saveStopGeo} disabled={stopSaving} data-testid="cal-stop-save"><MapPin className="mr-2 h-4 w-4" /> {stopSaving ? "Geolocalizzo…" : "Salva e geolocalizza"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!endEdit} onOpenChange={(o) => { if (!o) setEndEdit(null); }}>
        <DialogContent data-testid="cal-endpoint-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> {endEdit?.which === "rientro" ? "Rientro" : "Partenza"} · {endEdit?.agent}</DialogTitle>
            <DialogDescription>{!endEdit?.scope ? "Cosa vuoi modificare?" : (endEdit.scope === "sede" ? "Aggiorna la sede: vale per tutti i tragitti." : "Imposta il punto solo per questo giorno.")}</DialogDescription>
          </DialogHeader>
          {endEdit && !endEdit.scope && (
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => setEndEdit((s) => ({ ...s, scope: "trip" }))} data-testid="cal-endpoint-scope-trip">Modifica solo per questo tragitto</Button>
              {endEdit.is_agency && endEdit.agency_id && (
                <Button variant="outline" className="w-full justify-start" onClick={() => setEndEdit((s) => ({ ...s, scope: "sede" }))} data-testid="cal-endpoint-scope-sede">Modifica la sede (vale sempre)</Button>
              )}
              {!endEdit.is_agency && <p className="text-[11px] text-muted-foreground">La sede aziendale non è modificabile da qui: puoi impostare il punto solo per questo tragitto.</p>}
            </div>
          )}
          {endEdit?.scope && (
            <div className="space-y-3">
              <div className="relative space-y-1.5"><Label className="text-xs">Indirizzo</Label><Input value={endEdit.address} onChange={(e) => setEndEdit((s) => ({ ...s, address: e.target.value, lat: null, lng: null }))} placeholder="Via e numero civico, città" className="text-base sm:text-sm" data-testid="cal-endpoint-address" />
                <EndpointSuggest q={[endEdit.address, endEdit.city].filter(Boolean).join(" ")} picked={endEdit.lat != null}
                  onPick={(s) => { const [via, ...rest] = s.label.split(", "); const city = (rest.join(", ").replace(/^\d{5}\s*/, "") || endEdit.city); setEndEdit((o) => ({ ...o, address: via, city, lat: s.lat, lng: s.lng })); }} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-1.5"><Label className="text-xs">Città</Label><Input value={endEdit.city} onChange={(e) => setEndEdit((s) => ({ ...s, city: e.target.value, lat: null, lng: null }))} className="text-base sm:text-sm" data-testid="cal-endpoint-city" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Provincia</Label><Input value={endEdit.province} onChange={(e) => setEndEdit((s) => ({ ...s, province: e.target.value }))} placeholder="BS" className="text-base sm:text-sm" data-testid="cal-endpoint-prov" /></div>
              </div>
              {endEdit.lat != null && <p className="text-[11px] text-emerald-500" data-testid="cal-endpoint-picked">Posizione selezionata dal suggerimento</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndEdit(null)}>Annulla</Button>
            {endEdit?.scope && <Button onClick={saveEndEdit} disabled={endSaving} data-testid="cal-endpoint-save"><MapPin className="mr-2 h-4 w-4" /> {endSaving ? "Salvo…" : "Salva e geolocalizza"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
